import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _isObject from 'lodash/isObject';
import _isString from 'lodash/isString';

import Utils from '../../../../libs/utils';
import { SOURCE_TYPE_DEFAULT } from '../constants/sources';

function removeQueryParams(url) {
  if (!_isString(url) || _isEmpty(url)) {
    return url;
  }

  const questionMarkIndex = url.indexOf('?');
  return questionMarkIndex !== -1 ? url.substring(0, questionMarkIndex) : url;
}

export function getImageUrl(image = {}, { params = {}, stripQueryParams = false } = {}) {
  let finalUrl = '';
  if (_isString(image)) {
    finalUrl = image;
  } else if (_isObject(image) && !_isEmpty(image)) {
    const { file: fileLocation = '', url = '', s3File = '' } = image;
    if (_isEmpty(fileLocation) || fileLocation.startsWith('/')) {
      finalUrl = url || s3File || '';
    } else if (_isString(fileLocation)) {
      finalUrl = fileLocation;
    }
  }

  if (stripQueryParams) {
    finalUrl = removeQueryParams(finalUrl);
  }

  if (params && Object.keys(params) && !_isEmpty(finalUrl) && !_isNil(finalUrl)) {
    finalUrl = Object.keys(params).reduce((intermediateUrl, paramName) => {
      const value = params[paramName];
      if (_isNil(value)) {
        return intermediateUrl;
      }
      return Utils.addImageQueryParam(intermediateUrl, paramName, value);
    }, finalUrl);
  }

  return finalUrl;
}

export function isPdfFile(url = '') {
  if (!_isString(url) || _isEmpty(url)) {
    return false;
  }

  const extension = url.slice(-4);
  return extension.toLowerCase() === '.pdf';
}

export function stringifyBytes(bytes, { decimals = 2, marker = 1024 } = {}) {
  if (bytes === 0) return '0 Bytes';

  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(marker));

  return `${parseFloat((bytes / marker ** i).toFixed(decimals))} ${sizes[i]}`;
}

export function createLocalFileObject(file = {}) {
  const finalFile = _get(file, 'file', file);
  return {
    name: finalFile.name,
    size: finalFile.size,
    url: window.URL.createObjectURL(finalFile),
    file: finalFile,
    source: finalFile.source || file.source || SOURCE_TYPE_DEFAULT,
  };
}

export function createFilePayload(file = {}) {
  return {
    file: file.file,
    filename: file.name,
    description: '',
    source: file.source || SOURCE_TYPE_DEFAULT,
  };
}

export function getFileNameAndExtension(url = '') {
  const { pathname } = new URL(url);
  const fileNameWithExtension = pathname.split('/').pop() || 'default.png';
  const [fileName, extension = 'png'] = fileNameWithExtension.split(/\.(?=[^.]+$)/);

  return { fileName, extension };
}

/**
 * Downloads an image from a URL and converts it to a File object.
 *
 * @param {string} url - The URL of the image to download (e.g., 'https://example.com/photo.jpg')
 * @returns {Promise<File|null>} A Promise that resolves to a File object with name 'photo.jpg' and image data, or null if URL is empty
 */
export async function downloadImage(url) {
  if (_isEmpty(url)) {
    return null;
  }
  const { fileName, extension } = getFileNameAndExtension(url);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = url;
    img.crossOrigin = 'Anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        canvas.remove();
        const file = new File([blob], `${fileName}.${extension}`, { type: blob.type });
        resolve(file);
      }, `image/${extension}`);
    };

    img.onerror = (err) => reject(err);
  });
}

/**
 * Converts a remote image URL to a base64 data URL format.
 *
 * This is useful when you need to:
 * - Compare a remote image with a locally edited version (both in data URL format)
 * - Create a self-contained image representation that doesn't depend on external URLs
 *
 * @param {string} url - The URL of the image to convert (e.g., 'https://example.com/photo.jpg')
 * @param {string} [outputFormat='image/png'] - The output format for the data URL
 * @param {number} [quality=1] - The image quality (0-1) for lossy formats like JPEG
 * @returns {Promise<string|null>} A Promise that resolves to a base64 data URL string (e.g., 'data:image/png;base64,iVBORw0KGgo...') or null if URL is empty
 */
export async function urlToBase64(url, { outputFormat = 'image/png', quality = 1 } = {}) {
  if (_isEmpty(url)) {
    return null;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = url;
    img.crossOrigin = 'Anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);

      const dataUrl = canvas.toDataURL(outputFormat, quality);
      canvas.remove();
      resolve(dataUrl);
    };

    img.onerror = (err) => reject(err);
  });
}

/**
 * Converts a File object to a base64 data URL.
 *
 * @param {File} file - The File object to convert (e.g., from input[type="file"])
 * @returns {Promise<string>} A Promise that resolves to a base64 data URL string (e.g., 'data:image/jpeg;base64,/9j/4AAQSkZJRgAB...')
 */
export async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result);
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsDataURL(file);
  });
}

export function imageMimeTypeToExtension(mimeType) {
  const mimeTypes = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/bmp': 'bmp',
    'image/webp': 'webp',
  };

  return mimeTypes[mimeType];
}

export function dataUriToMimeType(dataUri) {
  return dataUri.split(',')[0].split(':')[1].split(';')[0];
}

/**
 * Converts a base64 data URI to a Blob object.
 *
 * @param {string} dataUri - The base64 data URI to convert (e.g., "data:image/png;base64,iVBORw0KGgo...")
 * @returns {Blob} A Blob object with type 'image/png' containing the decoded data
 */
export function base64ToBlob(dataUri) {
  const byteString = atob(dataUri.split(',')[1]);

  // separate out the mime component
  const mimeString = dataUriToMimeType(dataUri);

  // write the bytes of the string to an ArrayBuffer
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const intArray = new Uint8Array(arrayBuffer);
  for (let i = 0; i < byteString.length; i += 1) {
    intArray[i] = byteString.charCodeAt(i);
  }
  return new Blob([arrayBuffer], { type: mimeString });
}

/**
 * Converts a base64 data URI to a File object.
 *
 * @param {string} dataUri - The base64 data URI to convert (e.g., "data:image/png;base64,iVBORw0KGgo...")
 * @param {string} [filename='file'] - The name for the file, extension will be added if missing (e.g., 'my-image')
 * @returns {File} A File object with name 'my-image.png' and image data
 */
export function base64ToFile(dataUri, filename = 'file') {
  const mimeType = dataUriToMimeType(dataUri);
  let finalFilename = filename;
  if (filename.indexOf('.') === -1) {
    finalFilename = `${filename}.${imageMimeTypeToExtension(mimeType)}`;
  }
  return new File([base64ToBlob(dataUri)], finalFilename, { type: mimeType });
}

/**
 * Determines if a URL points to a remote resource (as opposed to a local one).
 *
 * Remote URLs are those that require a network request to fetch, such as HTTP/HTTPS URLs.
 * Local URLs include:
 * - blob: URLs (created by URL.createObjectURL)
 * - data: URLs (base64 encoded data URIs)
 * - file: URLs (local file system URLs)
 * - Relative paths (starting with / or no protocol)
 *
 * @param {string} url - The URL to check
 * @returns {boolean} True if the URL is remote, false if it's local or invalid
 */
export function isRemoteUrl(url) {
  if (!_isString(url)) {
    return false;
  }

  // Check for local URL patterns
  if (
    url.startsWith('blob:') ||
    url.startsWith('data:') ||
    url.startsWith('file:') ||
    url.startsWith('/') ||
    !url.includes('://')
  ) {
    return false;
  }

  // If it has a protocol and it's not a local one, consider it remote
  return true;
}
